import { enrichMyRelationshipAccount } from "../account-relationships/relationshipDataAdapter";
import {
  formatContactDisplayName,
  meddpiccRoleToMatrixColumn,
  normalizePairVendorFields,
  resolveContactMeddpiccMatrixMeta,
} from "../account-relationships/relationshipUtils";

const prefer = (...values) =>
  values.find(value => value !== undefined && value !== null && value !== "");

function attachMeddpiccFields(contact) {
  const { meddKey, meddRoleLabel, meddGroup } =
    resolveContactMeddpiccMatrixMeta(contact);
  return { meddKey, meddRoleLabel, meddGroup };
}

function mapWorkspaceContact(contactRow, accountId) {
  const contact = contactRow?.contact || {};
  const meddpiccRoleCanonical = prefer(
    contact.meddpicc_role,
    contact.role,
    "unknown"
  );
  const mapped = {
    id: String(contact.user_id || contact.id),
    accountId: String(accountId),
    name: formatContactDisplayName(contact),
    title: prefer(contact.position, contact.role, "Customer Contact"),
    meddpiccRoleCanonical,
    meddpiccRole: meddpiccRoleToMatrixColumn(meddpiccRoleCanonical),
    posture: "Unknown",
    status: contact.is_active === false ? "inactive" : "active",
    raw: contact,
    isMapped: Boolean(contactRow?.is_mapped),
  };
  return { ...mapped, ...attachMeddpiccFields(mapped) };
}

function mapWorkspacePair(contactRow, accountId, stakeholder) {
  const contact = contactRow?.contact || {};
  const mapping = contactRow?.mapping || {};
  const assessment = contactRow?.latest_assessment || null;
  const target = contactRow?.target || null;
  const contactId = String(contact.user_id || contact.id || "");
  const userId = String(stakeholder?.sourceId || mapping.user_id || "");

  return normalizePairVendorFields({
    id:
      userId && contactId
        ? `${userId}::${contactId}`
        : String(mapping.id || `${accountId}:${contactId}`),
    accountId: String(accountId),
    vendorType: "team_member",
    vendorSourceId: stakeholder?.sourceId ? String(stakeholder.sourceId) : null,
    vendorStakeholderId: stakeholder?.id ? String(stakeholder.id) : null,
    contactId,
    ladderLevel: 2,
    rlLevel: assessment?.rl ?? null,
    assessedPosture: assessment?.posture ?? null,
    rationale: assessment?.rationale ?? null,
    assessedAt: assessment?.assessed_at ?? null,
    goalTargetRl: prefer(assessment?.target_rl, target?.target_rl) ?? null,
    goalTargetHorizon:
      prefer(assessment?.target_date, target?.target_date) ?? null,
    relationshipGoal: null,
    isPrimaryOwner: true,
    isOrphaned: false,
    lastTouchDays: 0,
    signalStrength: 0,
    raw: {
      mapping,
      assessment,
      target,
      contact,
      executive_id: userId || mapping.user_id || null,
    },
  });
}

export function buildCurrentUserStakeholder(user = {}) {
  const userId = String(user.user_id || user.id || "");
  const grade = Number(prefer(user.grade, user.ladderLevel, user.ladder_level));
  return {
    id: userId ? `team-${userId}` : "team-current-user",
    sourceId: userId,
    accountId: "",
    name: prefer(user.user_name, user.name, "You"),
    title: prefer(user.title, user.role, "Account team"),
    ladderLevel:
      Number.isFinite(grade) && grade >= 1 && grade <= 5
        ? Math.round(grade)
        : null,
    role: prefer(user.role, ""),
    vendorType: "team_member",
    isCurrentUser: true,
    raw: user,
  };
}

export function computeMyLensRlAggregate(pairs = []) {
  const assessed = pairs.filter(
    pair => pair.rlLevel !== null && pair.rlLevel !== undefined
  );
  if (!assessed.length) return null;
  const sum = assessed.reduce(
    (total, pair) => total + Number(pair.rlLevel || 0),
    0
  );
  return sum / assessed.length;
}

/** Stable account-level RL — never the viewer's personal pair mean. */
function resolveAccountRlAggregate(summaryAccount) {
  if (
    summaryAccount?.accountRlAggregate !== undefined &&
    summaryAccount?.accountRlAggregate !== null &&
    summaryAccount?.accountRlAggregate !== ""
  ) {
    const stored = Number(summaryAccount.accountRlAggregate);
    return Number.isFinite(stored) ? stored : null;
  }

  const raw = prefer(
    summaryAccount?.raw?.aggregateRl,
    summaryAccount?.raw?.aggregateRlMedian,
    summaryAccount?.raw?.aggregate_rl
  );
  if (raw !== undefined && raw !== null && raw !== "") {
    const fromRaw = Number(raw);
    if (Number.isFinite(fromRaw)) return fromRaw;
  }

  // Portfolio row before mapping hydrate: rlAggregate is still account-level.
  if (!summaryAccount?.mappingsHydrated) {
    if (
      summaryAccount?.rlAggregate === undefined ||
      summaryAccount?.rlAggregate === null ||
      summaryAccount?.rlAggregate === ""
    ) {
      return null;
    }
    const fromSummary = Number(summaryAccount.rlAggregate);
    return Number.isFinite(fromSummary) ? fromSummary : null;
  }

  return null;
}

export function mapUserMappingAccountRow(
  accountRow,
  summaryAccount,
  stakeholder,
  index = 0
) {
  const accountId = String(accountRow.account_id || summaryAccount?.id || "");
  const contactRows = accountRow.contacts || [];
  const contacts = contactRows.map(row => mapWorkspaceContact(row, accountId));
  const myPairs = contactRows
    .filter(row => row.is_mapped && row.mapping)
    .map(row => mapWorkspacePair(row, accountId, stakeholder));

  // Drop-1: personal stats from filtered myPairs only (not account-wide mappedPairs).
  const myMappedPairs = myPairs.length;
  const myAssessedPairs = myPairs.filter(
    pair => pair.rlLevel !== null && pair.rlLevel !== undefined
  ).length;
  const myPendingAssessments = Math.max(0, myMappedPairs - myAssessedPairs);
  const personalRlAggregate = computeMyLensRlAggregate(myPairs);
  const accountRlAggregate = resolveAccountRlAggregate(summaryAccount);

  // Keep account-level pair totals from portfolio summary for non-personal UI.
  const accountMappedPairs = Number(summaryAccount?.mappedPairs) || 0;
  const accountAssessedPairs = Number(summaryAccount?.assessedPairs) || 0;
  const accountPendingAssessments =
    Number(summaryAccount?.pendingAssessments) ||
    Math.max(0, accountMappedPairs - accountAssessedPairs);

  const base = enrichMyRelationshipAccount(
    {
      ...summaryAccount,
      id: accountId,
      name: prefer(
        accountRow.account_name,
        summaryAccount?.name,
        "Unnamed Account"
      ),
      contacts,
      // Personal pairs for matrix / You stats; keep account totals separate.
      pairs: myPairs,
      myPairs,
      myMappedPairs,
      myAssessedPairs,
      myPendingAssessments,
      mappedPairs: accountMappedPairs,
      assessedPairs: accountAssessedPairs,
      pendingAssessments: accountPendingAssessments,
      personalRlAggregate,
      accountRlAggregate,
      // Account health pill / L0 distance — not personal lens mean.
      rlAggregate: accountRlAggregate,
      mappingsHydrated: true,
    },
    index
  );

  return base;
}

export function mergeUserMappingsIntoAccounts(accounts = [], workspace = {}) {
  const rowsById = new Map(
    (workspace.accounts || []).map(row => [String(row.account_id), row])
  );
  const stakeholder = buildCurrentUserStakeholder(workspace.user || {});

  return (accounts || []).map((account, index) => {
    const row = rowsById.get(String(account.id));
    if (!row) {
      return {
        ...account,
        mappingsHydrated: false,
        myPairs: account.myPairs || [],
        contacts: account.contacts || [],
      };
    }
    return mapUserMappingAccountRow(row, account, stakeholder, index);
  });
}

export function getMyContactRowsForAccount(account) {
  return buildRingsContactsForAccount(account, { includeUnmapped: false });
}

export function buildRingsContactsForAccount(
  account,
  { includeUnmapped = false } = {}
) {
  const contacts = account?.contacts || [];
  const myPairs = account?.myPairs || [];
  const rowsByContact = new Map();

  myPairs.forEach(pair => {
    const contactId = String(pair.contactId || "");
    if (!contactId) return;
    const contact = contacts.find(item => String(item.id) === contactId) || {
      id: contactId,
      name: "Contact",
      title: "Customer Contact",
    };
    rowsByContact.set(contactId, {
      id: contactId,
      name: contact.name,
      title: contact.title || "Customer Contact",
      rlLevel: pair.rlLevel,
      relationshipGoal: pair.relationshipGoal,
      isMapped: true,
      pair,
      stakeholder: account?.currentUserStakeholder || null,
      contact,
      ...attachMeddpiccFields(contact),
    });
  });

  const mapped = [...rowsByContact.values()];
  if (!includeUnmapped) return mapped;

  const mappedIds = new Set(mapped.map(contact => String(contact.id)));
  const unmapped = contacts
    .filter(contact => contact?.id && !mappedIds.has(String(contact.id)))
    .map(contact => ({
      id: String(contact.id),
      name: contact.name || "Contact",
      title: contact.title || "Customer Contact",
      rlLevel: null,
      relationshipGoal: null,
      isMapped: false,
      contact,
      ...attachMeddpiccFields(contact),
    }));

  return [...mapped, ...unmapped];
}

export function attachContactsToMyPairs(account) {
  const contacts = account?.contacts || [];
  const stakeholder = account?.currentUserStakeholder || null;
  return (account?.myPairs || []).map(pair => {
    const contact = contacts.find(
      item => String(item.id) === String(pair.contactId)
    ) || {
      id: pair.contactId,
      name: "Contact",
      title: "Customer Contact",
    };
    return { ...pair, contact, stakeholder };
  });
}
